import { type IGoogleAnalyticsTrackingType, type IMediaCard } from '@pcln/contentful-components'
import { Button } from '@pcln/horizon'
import { type ReactNode, type SyntheticEvent } from 'react'

import analytics from '@/shared-utils/analytics'
import { hasContentTypeId } from '@/shared-utils/content-model-type-guards'
import type { ModalContent } from '@/types'

export function fireSelectPromotion(gaTracking?: IGoogleAnalyticsTrackingType) {
  const ga4Parameters = gaTracking?.parameters
  if (ga4Parameters) {
    analytics.fireGA4Event({
      event: 'select_promotion',
      attributes: { ...ga4Parameters.attributes },
    })
  }
}

type CallToActionField =
  | NonNullable<IMediaCard['callToAction']>
  | NonNullable<IMediaCard['primaryCallToAction']>

/** Props to spread onto Horizon MediaCard for the CTA area. */
export type MediaCardCallToActionProps = {
  role?: 'link' | 'button'
  href?: string
  callToActionText?: string
  onClick?: (e: SyntheticEvent) => void
  anchorProps?: { target?: string; rel?: string }
  ariaLabel?: string
  callToActionSlot?: ReactNode
}

/**
 * Maps a Contentful CTA to Horizon MediaCard props.
 *
 * Modal → `callToActionSlot` only (a Button inside the card opens MoreInfoModal).
 * Link / button / callToAction → card-level `role`, `href`, and `callToActionText`
 * (the whole card is clickable; avoids nested anchors).
 */
export function getCallToActionProps({
  callToAction,
  onModalOpen,
}: {
  callToAction?: CallToActionField
  onModalOpen: (modalProps: ModalContent, gaTracking?: IGoogleAnalyticsTrackingType) => void
}): MediaCardCallToActionProps {
  if (!callToAction || !hasContentTypeId(callToAction)) return {}

  // ── Modal: only the slot is interactive ──────────────────────────────────
  if (callToAction.contentTypeId === 'modal') {
    return {
      callToActionSlot: (
        <Button
          buttonType='button'
          size='sm'
          type='secondary'
          onClick={(e: SyntheticEvent) => {
            e.stopPropagation()
            onModalOpen(
              {
                heading: callToAction.modalContent.heading,
                paragraph: callToAction.modalContent.body,
                disclaimer: callToAction.modalContent.disclaimer,
              },
              callToAction.analyticsTracking,
            )
          }}
        >
          {callToAction.buttonText}
        </Button>
      ),
    }
  }

  // ── Navigable CTAs: the card wrapper handles the click ───────────────────
  const isNavigable =
    callToAction.contentTypeId === 'link' ||
    callToAction.contentTypeId === 'button' ||
    callToAction.contentTypeId === 'callToAction'

  if (!isNavigable) return {}

  const isButton =
    callToAction.contentTypeId === 'button' ||
    (callToAction.contentTypeId === 'callToAction' && callToAction.actionType === 'Button')

  const role = isButton ? 'button' : 'link'
  const callToActionText =
    callToAction.contentTypeId === 'button' ? callToAction.buttonText : callToAction.label
  const url = 'longUrl' in callToAction ? callToAction.longUrl || callToAction.url : callToAction.url
  const { ariaLabel, openInNewTab, analyticsTracking } = callToAction

  return {
    role,
    href: role === 'link' ? url : undefined,
    callToActionText,
    ariaLabel,
    anchorProps:
      role === 'link' && openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : undefined,
    onClick: (e: SyntheticEvent) => {
      e.stopPropagation()
      fireSelectPromotion(analyticsTracking)

      // Buttons have no href — navigate manually when a URL is present
      if (role === 'button' && url && globalThis.window !== undefined) {
        if (openInNewTab) {
          globalThis.window.open(url, '_blank', 'noopener,noreferrer')
        } else {
          globalThis.window.location.assign(url)
        }
      }
    },
  }
}
